// Global Instructions Rule Applied!
//
// Account form — Cap Table tab (Phase 4, signed-off Mockup 4). Versioned
// per-company snapshots: headline tiles with vs-prior deltas, the class view
// (issued / %, FD / %, our holding per fund DERIVED from asset deltas, our %
// of class), sparse holder detail behind row expansion, version history
// behind a History button, and the version's source documents. Snapshots are
// captured as drafts (editor modal), promoted to current (superseding the
// prior current), and corporate actions (splits) clone the current snapshot
// restated by an adjustment factor.
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Dropdown, Input, Modal, Popconfirm, Spin, Table, Tag } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DownOutlined, HistoryOutlined } from '@ant-design/icons'
import InputNumber from '../../../core/components/InputNumber.jsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { FileUploadManager } from '../../../core/components/FileUploadManager/index.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import {
  CapTableData,
  CapTableLine,
  CapTableTiles,
  CapTableVersionListEntry
} from '../../../state_manager/data_models/cap_table_model.ts'
import {
  CapTableSavePayload,
  createCapTableSplit,
  deleteCapTableVersion,
  fetchCapTable,
  setCapTableCurrent
} from '../services/dealExecutionService.ts'
import CapTableEditorModal from './CapTableEditorModal.tsx'

const TABLE_FILE_URLS_CAP_TABLE_VERSIONS = 'file_urls_cap_table_versions'
const FIELD_CAP_TABLE_VERSION_ID = 'cap_table_version_id'

const KIND_LABELS: Record<string, string> = {
  round: 'Round',
  corporate_action: 'Corporate action',
  ad_hoc: 'Ad hoc'
}

const STATUS_COLORS: Record<string, string> = {
  current: 'green',
  draft: 'blue',
  superseded: 'default'
}

export function shares(value: number | null | undefined): string {
  if (value == null) return '—'
  const n = Number(value)
  const digits = Number.isInteger(n) ? 0 : 2
  return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

export function pct(value: number | null | undefined): string {
  if (value == null) return '—'
  return `${Number(value).toFixed(2)}%`
}

function price(value: number | null | undefined): string {
  if (value == null) return '—'
  const n = Number(value)
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
}

interface TileProps {
  label: string
  value: string
  valueRight?: string | null
  sub?: string | null
  delta?: string | null
}

function Tile({ label, value, valueRight, sub, delta }: TileProps): React.ReactElement {
  return (
    // 16px body matches the FormSection standard (AntD small cards default to a tighter 12px)
    <Card size='small' styles={{ body: { padding: 16 } }}>
      <div className='text-xs text-gray-500'>{label}</div>
      <div className='flex items-baseline gap-3'>
        <div className='text-lg font-medium'>{value}</div>
        {valueRight ? <div className='text-lg font-medium'>{valueRight}</div> : null}
      </div>
      <div className='text-xs text-gray-400 min-h-[1rem]'>
        {sub}
        {sub && delta ? ' · ' : ''}
        {delta}
      </div>
    </Card>
  )
}

function tileDelta(
  current: number | null | undefined,
  prior: number | null | undefined
): string | null {
  if (current == null || prior == null) return null
  const d = Number(current) - Number(prior)
  if (d === 0) return 'unchanged vs prior'
  return `${d > 0 ? '+' : ''}${shares(d)} vs prior`
}

interface CapTableTabProps {
  accountId: string | null
  // Lets the host form's Refresh button reach this tab's own data fetch
  // (HoldingsTab convention on the company full form).
  onRefreshReady?: (refreshFn: () => Promise<void>) => void
}

export default function CapTableTab({
  accountId,
  onRefreshReady
}: CapTableTabProps): React.ReactElement {
  const { openFormInTab } = useTabManagerContext()
  const rootRef = useRef<HTMLDivElement>(null)
  const [data, setData] = useState<CapTableData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null)
  // Time-travel control: null = the cap table in force NOW with holdings
  // measured TODAY; a date moves both the version selection and the
  // holdings measurement to that date together.
  const [asOfDate, setAsOfDate] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [splitOpen, setSplitOpen] = useState(false)
  const [splitFactor, setSplitFactor] = useState<number | null>(null)
  const [splitAsOf, setSplitAsOf] = useState<string | null>(null)
  const [splitNotes, setSplitNotes] = useState('')
  const [splitSaving, setSplitSaving] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorInitial, setEditorInitial] = useState<CapTableSavePayload | null>(null)

  const refetch = useCallback(
    async (versionId: string | null = selectedVersionId): Promise<void> => {
      if (!accountId) return
      const token = getAccessToken(store.getState().auth)
      const result = await fetchCapTable(token, accountId, versionId, versionId ? null : asOfDate)
      if (result.error) showErrorToast(result.error)
      setData(result.data)
      setLoading(false)
    },
    [accountId, selectedVersionId, asOfDate]
  )

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!accountId) return
      const token = getAccessToken(store.getState().auth)
      const result = await fetchCapTable(
        token,
        accountId,
        selectedVersionId,
        selectedVersionId ? null : asOfDate
      )
      if (cancelled) return
      if (result.error) showErrorToast(result.error)
      setData(result.data)
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [accountId, selectedVersionId, asOfDate])

  useEffect(() => {
    if (onRefreshReady) onRefreshReady(() => refetch())
  }, [onRefreshReady, refetch])

  if (!accountId) return <></>
  if (loading || !data) {
    return (
      <div className='flex justify-center py-16'>
        <Spin />
      </div>
    )
  }

  const version = data.version
  const tiles: CapTableTiles | null | undefined = data.tiles
  const prior: CapTableTiles | null | undefined = data.prior_tiles
  const hasCurrent = data.versions.some((v) => v.cap_table_versions__status === 'current')

  // Id-free copy of the displayed version's lines — used when the editor
  // should CREATE a new version from what's on screen (copy).
  const copyDisplayedLines = (): CapTableSavePayload['lines'] =>
    data.lines.map((l) => ({
      security_ids: l.securities.map((s) => s.securities__id),
      class_name: l.cap_table_lines__class_name,
      issued: l.cap_table_lines__issued ?? null,
      fully_diluted: l.cap_table_lines__fully_diluted ?? null,
      issue_price: l.cap_table_lines__issue_price ?? null,
      is_esop_pool: l.cap_table_lines__is_esop_pool ?? false,
      line_kind: (l.cap_table_lines__line_kind || 'equity') as 'equity' | 'warrant' | 'convertible' | 'debt',
      sort_order: l.cap_table_lines__sort_order ?? 0,
      holders: l.holders.map((h) => ({
        holder_kind: h.cap_table_holdings__holder_kind as 'account' | 'text' | 'other',
        holder_account_id: h.accounts__id ?? null,
        holder_name: h.cap_table_holdings__holder_name ?? null,
        issued: h.cap_table_holdings__issued ?? null,
        fully_diluted: h.cap_table_holdings__fully_diluted ?? null,
        sort_order: h.cap_table_holdings__sort_order ?? 0
      }))
    }))

  const openNewVersion = (copyDisplayed: boolean): void => {
    const initial: CapTableSavePayload = {
      account_id: accountId,
      kind: 'round',
      round_id: null,
      as_of_date: dayjs().format('YYYY-MM-DD'),
      notes: null,
      lines: []
    }
    if (copyDisplayed && version) {
      initial.round_id = version.rounds__id ?? null
      initial.kind = version.cap_table_versions__kind
      initial.lines = copyDisplayedLines()
    }
    setEditorInitial(initial)
    setEditorOpen(true)
  }

  // Edit the displayed version IN PLACE — any status (owner decision
  // 2026-08-13): versions mark ECONOMIC events (round / corporate action /
  // ad-hoc snapshot); editing is refinement of the same snapshot. New
  // versions (and their supersession) are reserved for genuinely new
  // economic states via the New version menu.
  const openEdit = (): void => {
    if (!version) return
    setEditorInitial({
      id: version.cap_table_versions__id,
      account_id: accountId,
      round_id: version.rounds__id ?? null,
      kind: version.cap_table_versions__kind,
      as_of_date: version.cap_table_versions__as_of_date,
      notes: version.cap_table_versions__notes ?? null,
      lines: data.lines.map((l) => ({
        id: l.cap_table_lines__id,
        security_ids: l.securities.map((s) => s.securities__id),
        class_name: l.cap_table_lines__class_name,
        issued: l.cap_table_lines__issued ?? null,
        fully_diluted: l.cap_table_lines__fully_diluted ?? null,
        issue_price: l.cap_table_lines__issue_price ?? null,
        is_esop_pool: l.cap_table_lines__is_esop_pool ?? false,
        line_kind: (l.cap_table_lines__line_kind || 'equity') as 'equity' | 'warrant' | 'convertible' | 'debt',
        sort_order: l.cap_table_lines__sort_order ?? 0,
        holders: l.holders.map((h) => ({
          id: h.cap_table_holdings__id,
          holder_kind: h.cap_table_holdings__holder_kind as 'account' | 'text' | 'other',
          holder_account_id: h.accounts__id ?? null,
          holder_name: h.cap_table_holdings__holder_name ?? null,
          issued: h.cap_table_holdings__issued ?? null,
          fully_diluted: h.cap_table_holdings__fully_diluted ?? null,
          sort_order: h.cap_table_holdings__sort_order ?? 0
        }))
      }))
    })
    setEditorOpen(true)
  }

  const promote = async (versionId: string): Promise<void> => {
    const token = getAccessToken(store.getState().auth)
    const result = await setCapTableCurrent(token, versionId)
    if (!result.success) {
      showErrorToast(result.error || 'Failed to set version current')
      return
    }
    toast.success('Version is now current')
    setSelectedVersionId(versionId)
    await refetch(versionId)
  }

  const removeVersion = async (versionId: string): Promise<void> => {
    const token = getAccessToken(store.getState().auth)
    const result = await deleteCapTableVersion(token, versionId)
    if (!result.success) {
      showErrorToast(result.error || 'Failed to delete version')
      return
    }
    toast.success('Version deleted')
    if (selectedVersionId === versionId) setSelectedVersionId(null)
    await refetch(selectedVersionId === versionId ? null : selectedVersionId)
  }

  const recordSplit = async (): Promise<void> => {
    if (!splitFactor || !splitAsOf) return
    setSplitSaving(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const result = await createCapTableSplit(token, {
        accountId,
        factor: splitFactor,
        asOf: splitAsOf,
        notes: splitNotes || null
      })
      if (!result.versionId) {
        showErrorToast(result.error || 'Failed to record split')
        return
      }
      toast.success('Split recorded as a draft version — review, then set current')
      setSplitOpen(false)
      setSplitFactor(null)
      setSplitAsOf(null)
      setSplitNotes('')
      setSelectedVersionId(result.versionId)
      await refetch(result.versionId)
    } finally {
      setSplitSaving(false)
    }
  }

  const newVersionMenu = {
    items: [
      ...(version ? [{ key: 'copy', label: 'New version (copy displayed)' }] : []),
      { key: 'blank', label: 'New version (blank)' }
    ],
    onClick: ({ key }: { key: string }) => openNewVersion(key === 'copy')
  }

  // Soft over-allocation check (owner 2026-08-14): class totals are
  // authoritative and holder rows are sparse detail, so nothing blocks —
  // but named holders summing past the class total is almost certainly a
  // typo and deserves a flag.
  const holderOverAllocation = (line: CapTableLine): string | null => {
    const sumIssued = line.holders.reduce((n, h) => n + (h.cap_table_holdings__issued || 0), 0)
    const sumFd = line.holders.reduce((n, h) => n + (h.cap_table_holdings__fully_diluted || 0), 0)
    const overIssued =
      line.cap_table_lines__issued != null && sumIssued > Number(line.cap_table_lines__issued)
    const overFd =
      line.cap_table_lines__fully_diluted != null &&
      sumFd > Number(line.cap_table_lines__fully_diluted)
    if (!overIssued && !overFd) return null
    const parts: string[] = []
    if (overIssued) parts.push(`issued ${shares(sumIssued)} vs class ${shares(line.cap_table_lines__issued)}`)
    if (overFd) parts.push(`FD ${shares(sumFd)} vs class ${shares(line.cap_table_lines__fully_diluted)}`)
    return `Named holders exceed the class total (${parts.join('; ')}) — the class total stays authoritative; check the holder detail.`
  }

  // Our derived per-fund rows join the holder table with the same columns
  // (owner 2026-08-14) — kind 'ours' marks them; units come from booked
  // asset deltas, never typed, so they don't count toward over-allocation.
  const expandedHolderRows = (line: CapTableLine) => [
    ...line.prog__our_rows.map((r) => ({
      cap_table_holdings__id: `ours-${r.funds__id || 'fund'}`,
      cap_table_holdings__holder_kind: 'ours',
      accounts__id: null as string | null,
      accounts__name: null as string | null,
      cap_table_holdings__holder_name: r.funds__name ?? null,
      cap_table_holdings__issued: r.prog__units,
      cap_table_holdings__fully_diluted: r.prog__units,
      cap_table_holdings__sort_order: -1,
      funds__id: r.funds__id ?? null
    })),
    ...line.holders.map((h) => ({ ...h, funds__id: null as string | null }))
  ]

  const expandedRowRender = (line: CapTableLine): React.ReactElement => (
    // The -4px right margin cancels the expanded cell's right inset so the
    // sub-table's right edge meets the parent table's — the equal-share
    // columns then match the parent's shares exactly instead of drifting
    // a few pixels by the last column.
    <div className='space-y-3 py-1' style={{ marginRight: -4 }}>
      {holderOverAllocation(line) && (
        <Alert type='warning' showIcon message={holderOverAllocation(line)} />
      )}
      {expandedHolderRows(line).length > 0 ? (
        <Table
          size='small'
          rowKey='cap_table_holdings__id'
          pagination={false}
          // Column-for-column alignment with the parent Classes table
          // (owner 2026-08-17): Holder + Kind span exactly the Class
          // column (the expanded cell is inset by the 48px expand column;
          // Kind is just wide enough for its widest tag, 'Linked').
          // The seven width-less columns after Kind then mirror the
          // parent's seven equal-share numeric columns 1:1 — Issued,
          // % Outstanding, FD and % FD line up, % of Class sits under
          // Issue Price, and two blank spacers sit under Our Holding
          // and Our % of Class.
          tableLayout='fixed'
          dataSource={expandedHolderRows(line)}
          columns={[
            {
              title: 'Holder',
              key: 'holder',
              width: 235,
              render: (_, h) => {
                if (h.cap_table_holdings__holder_kind === 'ours') {
                  return (
                    <span className='cursor-pointer text-blue-600 hover:underline'
                      onClick={() =>
                        h.funds__id &&
                        openFormInTab('funds', h.funds__id, h.cap_table_holdings__holder_name || 'Fund')
                      }
                    >
                      {h.cap_table_holdings__holder_name || 'Our funds'}
                    </span>
                  )
                }
                if (h.cap_table_holdings__holder_kind === 'account') {
                  return (
                    <span className='cursor-pointer text-blue-600 hover:underline'
                      onClick={() =>
                        h.accounts__id &&
                        openFormInTab('companies', h.accounts__id, h.accounts__name || 'Account')
                      }
                    >
                      {h.accounts__name || 'Account'}
                    </span>
                  )
                }
                if (h.cap_table_holdings__holder_kind === 'other') {
                  return <span className='text-gray-500'>Other holders (aggregate)</span>
                }
                return h.cap_table_holdings__holder_name || '—'
              }
            },
            {
              title: 'Kind',
              dataIndex: 'cap_table_holdings__holder_kind',
              width: 70,
              render: (v) =>
                v === 'ours' ? (
                  <Tag color='purple'>Ours</Tag>
                ) : v === 'account' ? (
                  <Tag color='blue'>Linked</Tag>
                ) : v === 'other' ? (
                  <Tag>Other</Tag>
                ) : (
                  <Tag>Named</Tag>
                )
            },
            {
              title: 'Issued',
              dataIndex: 'cap_table_holdings__issued',
              align: 'right' as const,
              render: (v) => shares(v)
            },
            {
              title: '% Outstanding',
              key: 'pct_issued',
              align: 'right' as const,
              // Only equity lines are outstanding stock — holders of a
              // warrant / convertible / debt line never show an
              // outstanding %.
              render: (_, h) =>
                line.cap_table_lines__line_kind === 'equity' &&
                tiles?.issued_total &&
                h.cap_table_holdings__issued != null
                  ? pct((Number(h.cap_table_holdings__issued) / Number(tiles.issued_total)) * 100)
                  : '—'
            },
            {
              title: 'FD',
              dataIndex: 'cap_table_holdings__fully_diluted',
              align: 'right' as const,
              render: (v) => shares(v)
            },
            {
              title: '% FD',
              key: 'pct_fd',
              align: 'right' as const,
              // Equity and warrant lines feed the FD total; convertible /
              // debt holdings show no FD %.
              render: (_, h) =>
                ['equity', 'warrant'].includes(line.cap_table_lines__line_kind || 'equity') &&
                tiles?.fd_total &&
                h.cap_table_holdings__fully_diluted != null
                  ? pct((Number(h.cap_table_holdings__fully_diluted) / Number(tiles.fd_total)) * 100)
                  : '—'
            },
            {
              // Sits in line with the parent's Issue Price column.
              title: '% of Class',
              key: 'pct_class',
              align: 'right' as const,
              render: (_, h) =>
                line.cap_table_lines__issued && h.cap_table_holdings__issued != null
                  ? pct(
                      (Number(h.cap_table_holdings__issued) / Number(line.cap_table_lines__issued)) *
                        100
                    )
                  : '—'
            },
            // Blank spacers keep the grid in step with the parent's
            // Our Holding and Our % of Class columns.
            { title: '', key: 'spacer_our_holding', render: () => null },
            { title: '', key: 'spacer_our_pct_of_class', render: () => null }
          ]}
        />
      ) : (
        <div className='text-xs text-gray-400'>Class totals only — no holder detail captured.</div>
      )}
    </div>
  )

  // The app scrolls in the layout's overflow-y-auto container, not the window —
  // point AntD's sticky machinery at it so the summary row pins to the viewport.
  const getStickyContainer = (): HTMLElement | Window =>
    (rootRef.current?.closest('.overflow-y-auto') as HTMLElement) || window

  return (
    <div ref={rootRef} className='px-2 py-4 space-y-4'>
      {/* Header: selected-version context + actions */}
      <div className='flex items-center justify-between flex-wrap gap-2'>
        <div className='flex items-center gap-2'>
          {version ? (
            <>
              <Tag color={STATUS_COLORS[version.cap_table_versions__status] || 'default'}>
                {version.cap_table_versions__status}
              </Tag>
              <span className='text-sm font-medium'>
                {KIND_LABELS[version.cap_table_versions__kind] || version.cap_table_versions__kind}
                {version.rounds__name ? ` · ${version.rounds__name}` : ''}
              </span>
              <span className='text-xs text-gray-500'>
                as of {dayjs(version.cap_table_versions__as_of_date).format('MM/DD/YYYY')}
              </span>
              {version.cap_table_versions__adjustment_factor != null && (
                <Tag color='purple'>×{version.cap_table_versions__adjustment_factor} adjustment</Tag>
              )}
              {data.measured_at && (
                <span className='text-xs text-gray-400'>
                  · our holdings as of{' '}
                  {dayjs(data.measured_at).isSame(dayjs(), 'day')
                    ? 'today'
                    : dayjs(data.measured_at).format('MM/DD/YYYY')}
                </span>
              )}
            </>
          ) : (
            <span className='text-sm text-gray-500'>No cap-table versions yet</span>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <DatePicker
            size='small'
            placeholder='As of today'
            format='MM/DD/YYYY'
            allowClear
            value={asOfDate ? dayjs(asOfDate) : null}
            disabledDate={(d) => d.isAfter(dayjs(), 'day')}
            onChange={(d) => {
              // Picking a date time-travels version + holdings together;
              // clearing returns to the current version measured today.
              setSelectedVersionId(null)
              setAsOfDate(d ? d.format('YYYY-MM-DD') : null)
            }}
          />
          <Button size='small' icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
            History
          </Button>
          <Dropdown menu={newVersionMenu} trigger={['click']}>
            <Button size='small' type='primary'>
              New version <DownOutlined />
            </Button>
          </Dropdown>
          <Button size='small' disabled={!hasCurrent} onClick={() => setSplitOpen(true)}>
            Record split
          </Button>
          {version && (
            <Button size='small' onClick={openEdit}>
              Edit
            </Button>
          )}
          {version && version.cap_table_versions__status !== 'current' && (
            <Button size='small' onClick={() => promote(version.cap_table_versions__id)}>
              Set current
            </Button>
          )}
        </div>
      </div>

      {!version ? (
        <FormSection>
          <div className='text-sm text-gray-400 py-4'>
            Capture the company&apos;s first cap-table snapshot with &quot;New version&quot; — tie it
            to a funding round, a corporate action, or an ad-hoc date. Our own holdings are always
            derived from booked transactions and appear automatically.
          </div>
        </FormSection>
      ) : (
        <>
          {data.stale_splits.length > 0 && (
            <Alert
              type='warning'
              showIcon
              className='mb-3'
              message={`This cap-table version pre-dates a booked share split (${data.stale_splits
                .map(
                  (s) =>
                    `×${s.asset_transactions__split_factor ?? '?'} on ${dayjs(
                      s.asset_transactions__transaction_date
                    ).format('MM/DD/YYYY')}`
                )
                .join(
                  ', '
                )}) — its counts are pre-split while our holdings are measured after it, so percentages mix share bases. Record the split (or edit the version) to restate.`}
            />
          )}
          {/* Headline tiles — capped width so they don't stretch across wide screens */}
          <div className='grid grid-cols-2 xl:grid-cols-4 gap-3 max-w-4xl'>
            <Tile
              label='Our Holding (All Funds, FD-Common Equiv.)'
              value={tiles?.our_pct_fd != null ? pct(tiles.our_pct_fd) : '—'}
              valueRight={tiles?.our_units != null ? shares(tiles.our_units) : null}
              delta={tileDelta(tiles?.our_units, prior?.our_units)}
            />
            <Tile
              label='Fully Diluted'
              value={shares(tiles?.fd_total)}
              delta={tileDelta(tiles?.fd_total, prior?.fd_total)}
            />
            <Tile
              label='Outstanding'
              value={shares(tiles?.issued_total)}
              delta={tileDelta(tiles?.issued_total, prior?.issued_total)}
            />
            <Tile
              label='ESOP Reserve'
              value={shares(tiles?.esop_reserve)}
              delta={tileDelta(tiles?.esop_reserve, prior?.esop_reserve)}
            />
          </div>

          {/* Round reconciliation hint */}
          {version.rounds__id != null &&
            (version.rounds__issued_shares != null || version.rounds__fully_diluted_shares != null) && (
              <div className='text-xs text-gray-500'>
                Issued in round ({version.rounds__name}):{' '}
                {shares(version.rounds__issued_shares)} issued
                {version.rounds__fully_diluted_shares != null
                  ? ` · ${shares(version.rounds__fully_diluted_shares)} fully diluted`
                  : ''}{' '}
                — the round fact reconciling this snapshot with the prior one.
              </div>
            )}

          {/* Class view. overflowVisible keeps the card body free of overflow
              clipping so the summary row's position:sticky reaches the tab
              scroller; offsetHeader clears the section's own 32px sticky strip. */}
          <FormSection title='Classes' titleColor='blue' overflowVisible>
            <Table
              size='small'
              rowKey='cap_table_lines__id'
              pagination={false}
              dataSource={data.lines}
              // tableLayout fixed: the width-less numeric columns share the
              // remaining space EQUALLY (the original even distribution),
              // while the pinned expand (48) + Class (305) columns hold
              // their widths — the expanded holder sub-table pins
              // Holder + Kind to end at exactly the Class right edge.
              tableLayout='fixed'
              expandable={{ expandedRowRender, columnWidth: 48 }}
              sticky={{ offsetHeader: 32, getContainer: getStickyContainer }}
              summary={() => (
                <Table.Summary fixed='bottom'>
                  <Table.Summary.Row className='font-medium bg-gray-50'>
                    {/* index 0 is the expand-icon column */}
                    <Table.Summary.Cell index={0} />
                    <Table.Summary.Cell index={1}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align='right'>
                      {shares(tiles?.issued_total)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align='right'>
                      {tiles?.issued_total ? pct(100) : '—'}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align='right'>
                      {shares(tiles?.fd_total)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align='right'>
                      {tiles?.fd_total ? pct(100) : '—'}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} />
                    <Table.Summary.Cell index={7} align='right'>
                      {tiles?.our_units != null ? shares(tiles.our_units) : '—'}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={8} align='right'>
                      {tiles?.our_pct_fd != null ? pct(tiles.our_pct_fd) : '—'}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
              columns={[
                {
                  title: 'Class',
                  dataIndex: 'cap_table_lines__class_name',
                  // The pre-pin even-share width (~195) widened by the
                  // sub-table's Kind column (110): Holder + Kind beneath
                  // span exactly the expand + Class band.
                  width: 305,
                  render: (v, line) => (
                    <span>
                      {v}
                      {line.cap_table_lines__is_esop_pool ? <Tag className='ml-2'>ESOP</Tag> : null}
                      {line.cap_table_lines__line_kind === 'warrant' ? (
                        <Tag className='ml-2' color='geekblue'>Warrant</Tag>
                      ) : null}
                      {line.cap_table_lines__line_kind === 'convertible' ? (
                        <Tag className='ml-2' color='orange'>Convertible</Tag>
                      ) : null}
                      {line.cap_table_lines__line_kind === 'debt' ? (
                        <Tag className='ml-2' color='volcano'>Debt</Tag>
                      ) : null}
                      {line.securities.length > 0 && (
                        <div className='text-xs mt-0.5'>
                          {line.securities.map((s) => (
                            <span
                              key={s.securities__id}
                              className='cursor-pointer text-blue-600 hover:underline mr-2'
                              onClick={() =>
                                openFormInTab(
                                  'securities',
                                  s.securities__id,
                                  s.securities__name || 'Security'
                                )
                              }
                            >
                              {s.securities__name || 'Security'}
                            </span>
                          ))}
                        </div>
                      )}
                    </span>
                  )
                },
                {
                  title: 'Issued',
                  dataIndex: 'cap_table_lines__issued',
                  align: 'right' as const,
                  render: (v) => shares(v)
                },
                {
                  title: '% Outstanding',
                  dataIndex: 'prog__issued_pct',
                  align: 'right' as const,
                  render: (v) => pct(v)
                },
                {
                  title: 'FD',
                  dataIndex: 'cap_table_lines__fully_diluted',
                  align: 'right' as const,
                  render: (v) => shares(v)
                },
                {
                  title: '% FD',
                  dataIndex: 'prog__fd_pct',
                  align: 'right' as const,
                  render: (v) => pct(v)
                },
                {
                  title: 'Issue Price',
                  dataIndex: 'cap_table_lines__issue_price',
                  align: 'right' as const,
                  render: (v) => price(v)
                },
                {
                  title: 'Our Holding',
                  dataIndex: 'prog__our_units',
                  align: 'right' as const,
                  render: (v) => (v != null ? shares(v) : <span className='text-gray-300'>—</span>)
                },
                {
                  title: 'Our % of Class',
                  dataIndex: 'prog__our_pct_of_class',
                  align: 'right' as const,
                  render: (v) => (v != null ? pct(v) : <span className='text-gray-300'>—</span>)
                }
              ]}
            />
            {data.lines.some((l) => l.cap_table_lines__line_kind !== 'equity') && (
              <div className='mt-2 text-xs text-gray-400'>
                Warrant lines record instruments issued but not outstanding — they count toward
                fully diluted only. Convertible / debt lines are not stock — their counts are
                excluded from both the outstanding and fully-diluted totals.
              </div>
            )}
            {data.our_unmatched.length > 0 && (
              <div className='mt-2 text-xs text-amber-600'>
                Held securities not matched to a class line:{' '}
                {data.our_unmatched
                  .map((u) => `${u.securities__name || 'security'} (${u.funds__name || 'fund'}: ${shares(u.prog__units)})`)
                  .join(', ')}
              </div>
            )}
          </FormSection>

          {/* Source documents */}
          <FormSection title='Source Documents' titleColor='purple'>
            <FileUploadManager
              files={[]}
              tableName={TABLE_FILE_URLS_CAP_TABLE_VERSIONS}
              idField={FIELD_CAP_TABLE_VERSION_ID}
              idValue={version.cap_table_versions__id}
            />
          </FormSection>
        </>
      )}

      {/* History modal */}
      <Modal
        title='Cap Table History'
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={780}
      >
        <Table
          size='small'
          rowKey='cap_table_versions__id'
          pagination={false}
          dataSource={data.versions}
          columns={[
            {
              title: 'As Of',
              dataIndex: 'cap_table_versions__as_of_date',
              render: (v) => dayjs(String(v)).format('MM/DD/YYYY')
            },
            {
              title: 'Kind',
              dataIndex: 'cap_table_versions__kind',
              render: (v: string, row: CapTableVersionListEntry) => (
                <span>
                  {KIND_LABELS[v] || v}
                  {row.cap_table_versions__adjustment_factor != null
                    ? ` ×${row.cap_table_versions__adjustment_factor}`
                    : ''}
                </span>
              )
            },
            { title: 'Round', dataIndex: 'rounds__name', render: (v) => v || '—' },
            {
              title: 'Status',
              dataIndex: 'cap_table_versions__status',
              render: (v: string) => <Tag color={STATUS_COLORS[v] || 'default'}>{v}</Tag>
            },
            {
              title: 'Docs',
              dataIndex: 'prog__files_count',
              align: 'right' as const,
              render: (v) => v || 0
            },
            {
              title: 'Modified',
              dataIndex: 'cap_table_versions__date_modified',
              render: (v) => (v ? dayjs(String(v)).format('MM/DD/YYYY') : '—')
            },
            {
              title: '',
              key: 'actions',
              render: (_, row: CapTableVersionListEntry) => (
                <div className='flex gap-2'>
                  <span className='cursor-pointer text-blue-600 hover:underline'
                    onClick={() => {
                      setSelectedVersionId(row.cap_table_versions__id)
                      setHistoryOpen(false)
                    }}
                  >
                    View
                  </span>
                  {row.cap_table_versions__status !== 'current' && (
                    <>
                      <span className='cursor-pointer text-blue-600 hover:underline' onClick={() => promote(row.cap_table_versions__id)}>Set current</span>
                      <Popconfirm
                        title='Delete this version?'
                        onConfirm={() => removeVersion(row.cap_table_versions__id)}
                      >
                        <span className='cursor-pointer text-red-500 hover:underline'>Delete</span>
                      </Popconfirm>
                    </>
                  )}
                </div>
              )
            }
          ]}
        />
      </Modal>

      {/* Split modal */}
      <Modal
        title='Record Share Split'
        open={splitOpen}
        onCancel={() => setSplitOpen(false)}
        onOk={recordSplit}
        okText='Create draft'
        okButtonProps={{ disabled: !splitFactor || !splitAsOf, loading: splitSaving }}
      >
        <div className='space-y-3 py-2'>
          <div className='text-xs text-gray-500'>
            Clones the current snapshot with counts × factor and historical issue price ÷ factor.
            The clone lands as a draft for review.
          </div>
          <div className='flex items-center gap-3'>
            <span className='w-32 flex-none text-xs text-gray-500'>Factor</span>
            <InputNumber
              size='small'
              className='w-full'
              value={splitFactor ?? undefined}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                const raw = e.target.value.replace(/[^0-9.]/g, '')
                setSplitFactor(raw === '' ? null : Number(raw))
              }}
            />
          </div>
          <div className='flex items-center gap-3'>
            <span className='w-32 flex-none text-xs text-gray-500'>Effective date</span>
            <DatePicker
              size='small'
              className='w-full'
              format='MM/DD/YYYY'
              value={splitAsOf ? dayjs(splitAsOf) : null}
              onChange={(v) => setSplitAsOf(v ? v.format('YYYY-MM-DD') : null)}
            />
          </div>
          <div className='flex items-center gap-3'>
            <span className='w-32 flex-none text-xs text-gray-500'>Notes</span>
            <Input
              size='small'
              value={splitNotes}
              onChange={(e) => setSplitNotes(e.target.value)}
              placeholder='e.g. 10:1 forward split'
            />
          </div>
        </div>
      </Modal>

      {/* Draft editor */}
      <CapTableEditorModal
        open={editorOpen}
        accountId={accountId}
        initial={editorInitial}
        companyRounds={data.company_rounds}
        companySecurities={data.company_securities}
        roundParticipations={data.round_participations}
        onClose={() => setEditorOpen(false)}
        onSaved={async (versionId) => {
          setEditorOpen(false)
          setSelectedVersionId(versionId)
          await refetch(versionId)
        }}
      />
    </div>
  )
}
